// @flow
import memoizeOne from 'memoize-one';
import * as React from 'react';
import { Text } from 'react-native';
import { Input, ThemeContext } from 'react-native-elements';
import * as Yup from 'yup';
import { get } from 'lodash';
import {
    defineMessages,
} from 'react-intl';
import FieldWrapper from './FieldWrapper';
import { useFormattedFieldErrors } from './utils';

const styles = memoizeOne((theme) => ({
    label: {
        fontSize: theme.fontsizes.categoryTitle,
        color: theme.colors.defaultTextColor,
        marginHorizontal: 10,
        fontWeight: 'bold',
    },
    inputText: {
        color: theme.colors.defaultTextColor,
        fontSize: theme.fontsizes.text,
    },
    error: {
        color: theme.colors.danger,
        fontSize: theme.fontsizes.textSmall,
    },
}));

type Props = {
    field: {
        value: any,
        name: string,
    },
    form: {
        errors: Object,
        touched: Object,
        isSubmitting: boolean,
        handleChange: (name: string) => (value: string) => void,
        handleBlur: (name: string) => () => void,
    },
    required: boolean,
    label: string,
    help_text: string,
    disabled?: boolean,
}

const PasswordInput = (props: Props): React.Node => {
    const {
        disabled,
        field: { name, value },
        form: {
            errors, touched, isSubmitting,
            handleChange, handleBlur,
        },
        label,
        help_text: help,
        required,
    } = props;

    const { theme } = React.useContext(ThemeContext);

    let errs = get(errors, name);
    errs = useFormattedFieldErrors(errs);

    const onBlurCallback = React.useCallback(() => {
        handleBlur(name);
    }, [name, handleBlur]);

    return (
        <FieldWrapper
            required={required}
            helpText={help}
        >
            <Text style={styles(theme).label}>{required ? `${label} *` : label}</Text>
            <Input
                errorMessage={touched[name] && errs ? errs : undefined}
                errorStyle={styles(theme).error}
                onChangeText={handleChange(name)}
                placeholder={label}
                placeholderTextColor={theme.colors.defaultTextColor}
                onBlur={onBlurCallback}
                value={value || ''}
                disabled={disabled || isSubmitting}
                secureTextEntry
                // iOS will offer to fill the password from the device's keychain
                textContentType="password"
                // Android specific
                autoCompleteType="password"
                inputStyle={styles(theme).inputText}
            />
        </FieldWrapper>
    );
};

const messages = defineMessages({
    min: {
        id: 'passwordinput-min',
        defaultMessage: 'The length of this field must be more than {minLimit} characters!',
    },
    max: {
        id: 'passwordinput-max',
        defaultMessage: 'The length of this field must be less than {maxLimit} characters!',
    },
    required: {
        id: 'passwordinput-required',
        defaultMessage: 'This field is required.',
    },
});

PasswordInput.getValidationSchema = (field: { validation: Object }) => {
    let errMsg = Yup.string().nullable();
    if (field.validation.required) {
        errMsg = Yup.string().required(messages.required).nullable();
    }
    if (field.validation.min_length) {
        errMsg = errMsg.min(field.validation.min_length.toString(),
            { ...messages.min, values: { minLimit: field.validation.min_length.toString() } });
    }
    if (field.validation.max_length) {
        errMsg = errMsg.max(field.validation.max_length.toString(),
            { ...messages.max, values: { maxLimit: field.validation.max_length.toString() } });
    }

    return errMsg;
};

PasswordInput.defaultProps = {
    disabled: false,
};

export default PasswordInput;
